import React, {useCallback, useEffect, useState} from 'react';
import styled from 'styled-components';
import {useAuth} from '../../../contexts/AuthContext';
import {apiErrorMessage} from '../api/abfApi';
import {SmallButton} from '../onboarding/review/contractReviewStyles';
import {
  MAKER_ROLES, monthLabel, pct, plural, servicingApi, shortDate, usd, words,
  type Cohort, type Metric, type MonitoringWorkspace, type ServicingOverview,
} from './forwardFlowServicingApi';
import {Actions, Callout, Empty, Pane, Scroll, StateChip, Table} from './ForwardFlowServicingStyles';

const CARD_ORDER = ['ownedUpb', 'ownedLoans', 'dpd30PlusPct', 'dpd60PlusPct', 'dpd90PlusPct', 'cpr', 'smm', 'grossChargeOffs',
  'recoveries', 'annualizedNetLossPct', 'cumulativeNetLossPct', 'interestYieldPct', 'dueFromServicer'];
const BUCKETS = ['CURRENT', 'DPD_1_29', 'DPD_30_59', 'DPD_60_89', 'DPD_90_119', 'DPD_120_PLUS'];

const Cards = styled.div`
  display: grid; grid-template-columns: repeat(auto-fill, minmax(215px, 1fr)); gap: 12px; margin: 0 0 16px;
  article { border: 1px solid #2c4258; border-radius: 10px; background: #0d1a2b; padding: 14px; min-width: 0; }
  article[data-available='false'] { opacity: .8; border-style: dashed; }
  h3 { margin: 0; color: #9fb1c6; font-size: 11px; font-weight: 700; letter-spacing: .05em; text-transform: uppercase; }
  strong { display: block; margin: 6px 0 4px; font-size: 21px; font-variant-numeric: tabular-nums; }
  small { display: block; color: #8ea3ba; font-size: 12px; line-height: 1.45; }
  details summary { cursor: pointer; color: #9adce7; font-size: 12px; margin-top: 6px; }
`;
const Bars = styled.div`
  display: grid; gap: 8px;
  .row { display: grid; grid-template-columns: 160px 1fr 170px; gap: 10px; align-items: center; font-size: 13px; }
  .track { height: 10px; border-radius: 5px; background: #1b2b3e; overflow: hidden; }
  .fill { height: 100%; background: #38bdf8; }
  .fill[data-late='true'] { background: #f6c453; }
  .fill[data-severe='true'] { background: #f87171; }
  .num { text-align: right; font-variant-numeric: tabular-nums; color: #c9d7e6; }
  @media (max-width: 700px) { .row { grid-template-columns: 110px 1fr; } .num { grid-column: 1 / -1; text-align: left; } }
`;

const valueText = (m: Metric) => m.status !== 'AVAILABLE' || m.value === null || m.value === undefined ? 'Unavailable'
  : m.unit === 'USD' ? usd(m.value) : m.unit === 'PCT' ? pct(m.value, m.value !== 0 && Math.abs(m.value) < 1 ? 3 : 2) : Number(m.value).toLocaleString('en-US');

/**
 * Monitoring: performance of the owned loans. Every figure shows what it divides by, over
 * which population and window, and says so when it cannot be computed.
 */
export function ForwardFlowMonitoringWorkspace({programId}: {programId: number}) {
  const {user} = useAuth();
  const [data, setData] = useState<MonitoringWorkspace | null>(null);
  const [overview, setOverview] = useState<ServicingOverview | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try { const [m, o] = await Promise.all([servicingApi.monitoring(programId), servicingApi.overview(programId)]); setData(m); setOverview(o); setError(''); }
    catch (e) { setError(apiErrorMessage(e, 'Monitoring could not be loaded.')); }
  }, [programId]);
  useEffect(() => { void load(); }, [load]);
  const openPeriod = overview?.periods.find(p => p.state !== 'SIGNED_OFF' && p.steps.some(s => s.key === 'EVIDENCE' && s.state === 'DONE'));
  const refresh = async () => {
    if (!openPeriod) return;
    setBusy(true);
    try { setData(await servicingApi.runMonitoring(programId, openPeriod.id)); setError(''); }
    catch (e) { setError(apiErrorMessage(e, 'Metrics could not be refreshed.')); }
    finally { setBusy(false); }
  };
  if (!data) return <Pane><p className="muted">{error || 'Loading monitoring…'}</p></Pane>;
  const current = data.current;
  const refreshButton = openPeriod && MAKER_ROLES.has(user?.role ?? '') &&
    <SmallButton disabled={busy} onClick={() => void refresh()}>Preview {openPeriod.label} (provisional)</SmallButton>;
  if (!current) {
    return <>{error && <Callout $tone="error">{error}</Callout>}
      <Empty title="No monitoring run yet">Metrics are frozen when a servicing period is signed off. {openPeriod ? 'You can preview the open period now.' : ''}</Empty>
      {refreshButton && <Actions>{refreshButton}</Actions>}</>;
  }
  const metrics = current.metrics as Record<string, Metric & Record<string, any>>;
  const dist = metrics.delinquencyDistribution as unknown as {status: string; buckets: Record<string, {loans: number; upb: number}>} | undefined;
  const distTotal = dist ? BUCKETS.reduce((t, b) => t + Number(dist.buckets[b]?.upb ?? 0), 0) : 0;
  const rolls = metrics.rollRates as unknown as {status: string; reason?: string; matrix?: Record<string, Record<string, number>>; fromCounts?: Record<string, number>} | undefined;
  const timeliness = metrics.servicerTimeliness as unknown as Record<string, any> | undefined;
  return <>
    {error && <Callout $tone="error" role="alert">{error}</Callout>}
    <Pane>
      <header><div><h2>{current.label} · as of {shortDate(current.asOf)}</h2>
        <p>{current.basis === 'SIGNED_OFF' ? 'Frozen at sign-off.' : 'Provisional: the period is not signed off, so these figures can still change.'} Formula set {data.formulaVersion}.
          {' '}{current.coverage.statement}</p></div>
        <Actions><StateChip state={current.basis === 'SIGNED_OFF' ? 'SIGNED_OFF' : 'OPEN'} />{refreshButton}</Actions></header>
      <Cards>{CARD_ORDER.filter(k => metrics[k]).map(k => {
        const m = metrics[k];
        const available = m.status === 'AVAILABLE';
        return <article key={k} data-available={available}>
          <h3>{m.label}</h3><strong>{valueText(m)}</strong>
          {available ? <>
            {m.numerator !== null && m.numerator !== undefined && m.denominator !== null && m.denominator !== undefined &&
              <small>{m.operator === 'MINUS' ? `${usd(m.numerator)} booked − ${usd(m.denominator)} confirmed`
                : `${m.unit === 'COUNT' ? m.numerator : usd(m.numerator)} ÷ ${usd(m.denominator)}`}</small>}
            {(m.population || m.window) && <small>{m.population}{m.window ? ` · ${m.window}` : ''}</small>}
            {m.formula && <details><summary>How it is calculated</summary><small>{m.formula}</small></details>}
          </> : <small>{m.reason}</small>}
        </article>;
      })}</Cards>
    </Pane>
    {dist && dist.status === 'AVAILABLE' && <Pane>
      <header><div><h2>Delinquency</h2><p>Owned loans reported by the servicer, by official UPB.</p></div></header>
      <Bars>{BUCKETS.map((b, i) => {
        const upb = Number(dist.buckets[b]?.upb ?? 0);
        const share = distTotal > 0 ? upb / distTotal * 100 : 0;
        return <div className="row" key={b}><span>{words(b)}</span>
          <div className="track" role="img" aria-label={`${words(b)} ${share.toFixed(1)} percent`}><div className="fill" data-late={i >= 2 && i < 4} data-severe={i >= 4} style={{width: `${share}%`}} /></div>
          <span className="num">{plural(dist.buckets[b]?.loans, 'loan')} · {usd(upb, 0)} · {share.toFixed(1)}%</span></div>;
      })}</Bars>
    </Pane>}
    <Pane>
      <header><div><h2>Delinquency roll rates</h2><p>How loans moved between buckets since the previous period, by count.</p></div></header>
      {rolls?.status !== 'AVAILABLE' ? <p>{rolls?.reason ?? 'Unavailable.'}</p> :
      <Scroll><Table><thead><tr><th>From \ To</th>{BUCKETS.map(b => <th key={b} className="num">{words(b)}</th>)}</tr></thead>
        <tbody>{BUCKETS.filter(b => rolls.fromCounts?.[b]).map(from => <tr key={from}><td>{words(from)} ({rolls.fromCounts?.[from]})</td>
          {BUCKETS.map(to => { const n = rolls.matrix?.[from]?.[to] ?? 0; const total = rolls.fromCounts?.[from] ?? 0;
            return <td key={to} className="num">{n ? `${(n / total * 100).toFixed(0)}%` : '—'}</td>; })}</tr>)}</tbody></Table></Scroll>}
    </Pane>
    <Pane>
      <header><div><h2>Cohorts</h2><p>{current.cohorts.note}</p></div></header>
      <CohortTable title="Purchase cohorts (month bought)" rows={current.cohorts.purchaseCohorts} />
      <CohortTable title="Origination vintages (month originated)" rows={current.cohorts.originationVintages} />
    </Pane>
    {timeliness && <Pane>
      <header><div><h2>Servicer timeliness</h2></div></header>
      {timeliness.status !== 'AVAILABLE' ? <p>No report or remittance has arrived for this period yet.</p> :
      <Table style={{maxWidth: 700}}><thead><tr><th>Obligation</th><th>Due</th><th>Arrived</th><th>Status</th></tr></thead><tbody>
        <tr><td>Servicer report</td><td>{shortDate(timeliness.reportDue)}</td>
          <td>{timeliness.reportReceived ? `received ${shortDate(timeliness.reportReceived)}` : 'not yet received'}</td>
          <td><Timeliness due={timeliness.reportDue} arrived={timeliness.reportReceived} /></td></tr>
        <tr><td>Bank remittance</td><td>{shortDate(timeliness.remittanceDue)}</td>
          <td>{timeliness.remittanceValueDate ? `value dated ${shortDate(timeliness.remittanceValueDate)}` : 'not yet confirmed'}</td>
          <td><Timeliness due={timeliness.remittanceDue} arrived={timeliness.remittanceValueDate} /></td></tr>
      </tbody></Table>}
    </Pane>}
    {data.series.length > 0 && <Pane>
      <header><div><h2>Trend</h2><p>One row per period; provisional rows can still change.</p></div></header>
      <Scroll><Table><thead><tr><th>Period</th><th>Basis</th><th className="num">Owned UPB</th><th className="num">30+ DPD</th><th className="num">60+ DPD</th>
        <th className="num">90+ DPD</th><th className="num">CPR</th><th className="num">Annualized net loss</th><th className="num">Cumulative net loss</th></tr></thead>
        <tbody>{data.series.map(s => <tr key={s.periodId}><td>{s.label}</td><td>{s.basis === 'SIGNED_OFF' ? 'Signed off' : 'Provisional'}</td>
          <td className="num">{usd(s.ownedUpb, 0)}</td><td className="num">{pct(s.dpd30PlusPct)}</td><td className="num">{pct(s.dpd60PlusPct)}</td>
          <td className="num">{pct(s.dpd90PlusPct)}</td><td className="num">{pct(s.cpr)}</td><td className="num">{pct(s.annualizedNetLossPct)}</td>
          <td className="num">{pct(s.cumulativeNetLossPct, 3)}</td></tr>)}</tbody></Table></Scroll>
    </Pane>}
  </>;
}

/** On time, late by N days, or awaited, on business dates. */
function Timeliness({due, arrived}: {due?: string | null; arrived?: string | null}) {
  if (!arrived || !due) return <StateChip state="AWAITED" />;
  const days = Math.round((Date.parse(arrived.slice(0, 10)) - Date.parse(due.slice(0, 10))) / 86_400_000);
  return days <= 0 ? <StateChip state="ON_TIME" />
    : <><StateChip state="LATE" /> <span className="muted">{plural(days, 'day')} late</span></>;
}

function CohortTable({title, rows}: {title: string; rows: Cohort[]}) {
  return <>
    <h3 style={{fontSize: 13, margin: '10px 0 6px'}}>{title}</h3>
    {rows.length === 0 ? <p>No loans yet.</p> :
    <Scroll><Table><thead><tr><th>Cohort</th><th className="num">Loans</th><th className="num">Purchased UPB</th><th className="num">Current UPB</th>
      <th className="num">Factor</th><th className="num">30+ DPD</th><th className="num">Cumulative net loss</th></tr></thead>
      <tbody>{rows.map(c => <tr key={c.cohort}><td>{monthLabel(c.cohort)}</td><td className="num">{c.loans}</td><td className="num">{usd(c.purchasedUpb, 0)}</td>
        <td className="num">{usd(c.currentUpb, 0)}</td><td className="num">{c.factor == null ? '—' : Number(c.factor).toFixed(4)}</td>
        <td className="num">{pct(c.dpd30PlusPct)}</td><td className="num">{pct(c.cumulativeNetLossPct, 3)}</td></tr>)}</tbody></Table></Scroll>}
  </>;
}
